import type { Profile } from '../core/types';
import { fmtNum } from '../core/util';
import type { State } from '../state/store';
import { INDICES } from '../render/indices';
import { canClip } from '../render/style';
import {
  classificationStats, classificationGeoJSON, spectraCSV,
  classSummary, classSummaryCSV, classificationByClassGeoJSON,
} from '../export/classification';
import { isClassifying } from '../render/style';

function download(name: string, text: string, type = 'application/json') {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([text], { type }));
  a.download = name;
  a.click();
  URL.revokeObjectURL(a.href);
}
const baseName = (p: Profile) => (p.url.split('/').pop() || 'cog').replace(/\.[^.]+$/, '');

export function ExportPanel(
  { state, profile, onStatus }:
  { state: State; profile: Profile; onStatus: (m: string, err?: boolean) => void },
) {
  const summary = isClassifying(state) ? classSummary(profile, state) : null;
  const st = canClip(state) ? classificationStats(profile, state) : null;
  const what = state.mode === 'index'
    ? INDICES.find((i) => i.id === state.indexId)?.name ?? state.indexId
    : `band ${state.singleBand + 1}`;
  return (
    <section>
      <h2>Export</h2>
      {summary && (
        <>
          {summary.classes.map((c) => (
            <div class="kvrow" key={c.index}>
              <span><i class="sw" style={{ background:
                `rgb(${c.colour[0]},${c.colour[1]},${c.colour[2]})` }} />{c.name}</span>
              <b>{c.areaHa != null ? `${c.areaHa.toFixed(2)} ha` : `${c.cells} cells`}</b>
            </div>
          ))}
          <div class="kvrow"><span>unclassified</span>
            <b>{summary.valid ? (100 - summary.classified / summary.valid * 100).toFixed(1) : '0'}%</b>
          </div>
          <p class="hint">
            Areas are estimated from the sampled {summary.w}×{summary.h} overview
            {summary.approximate ? ', in a geographic CRS, so they are approximate.' : '.'}
            {' '}Unclassified ground is a result in its own right — if it is most of the
            scene, either widen the maximum angle or train a class for it.
          </p>
          <button class="btn wide" data-testid="export-classes" onClick={() => {
            void classificationByClassGeoJSON(profile, state).then((fc) => {
              const n = (fc?.['features'] as unknown[] | undefined)?.length ?? 0;
              if (!n) return onStatus('Nothing classified to export.', true);
              download(`${baseName(profile)}.classes.geojson`, JSON.stringify(fc, null, 1));
              onStatus(`Exported ${n} region${n > 1 ? 's' : ''} across ${summary.classes.length} classes.`);
            });
          }}>Download classified regions (GeoJSON)</button>
          <button class="btn wide" onClick={() =>
            download(`${baseName(profile)}.classes.csv`, classSummaryCSV(profile, state)!, 'text/csv')}>
            Download class areas (CSV)
          </button>
        </>
      )}
      {!st && !summary &&
        <p class="hint">Switch to an index or single-band view to export a classification.</p>}
      {st && (
        <>
          <div class="kvrow"><span>within range</span><b>{(st.fraction * 100).toFixed(1)}%</b></div>
          {st.areaHa != null && <>
            <div class="kvrow"><span>area</span><b>{st.areaHa.toFixed(2)} ha</b></div>
            <div class="kvrow"><span></span><b>{st.areaAcres!.toFixed(2)} acres</b></div>
          </>}
          {st.meanValue != null &&
            <div class="kvrow"><span>mean value</span><b>{fmtNum(st.meanValue)}</b></div>}
          <div class="kvrow"><span>sampled</span><b>{st.grid.w}×{st.grid.h}</b></div>
          <p class="hint">
            {what}, {fmtNum(Math.min(state.lo, state.hi))} to {fmtNum(Math.max(state.lo, state.hi))}.
            Area is estimated from the sampled overview
            {st.approximate ? ', and from a geographic CRS, so it is approximate.' : '.'}
          </p>
          <button class="btn wide" onClick={() => {
            void classificationGeoJSON(profile, state).then((fc) => {
              const n = (fc?.['features'] as unknown[] | undefined)?.length ?? 0;
              if (!n) return onStatus('Nothing inside the current range to export.', true);
              download(`${baseName(profile)}.${state.mode === 'index' ? state.indexId : 'band'}.geojson`,
                JSON.stringify(fc, null, 1));
              onStatus(`Exported ${n} region${n > 1 ? 's' : ''}.`);
            });
          }}>Download regions (GeoJSON)</button>
          <button class="btn wide" onClick={() => {
            const rows = [
              ['source', 'mode', 'index', 'min', 'max', 'within_range_pct', 'area_ha', 'area_acres', 'mean_value', 'sampled'].join(','),
              [JSON.stringify(profile.url), state.mode, state.mode === 'index' ? state.indexId : '',
                Math.min(state.lo, state.hi), Math.max(state.lo, state.hi),
                (st.fraction * 100).toFixed(3),
                st.areaHa?.toFixed(4) ?? '', st.areaAcres?.toFixed(4) ?? '',
                st.meanValue?.toPrecision(6) ?? '', `${st.grid.w}x${st.grid.h}`].join(','),
            ];
            download(`${baseName(profile)}.stats.csv`, rows.join('\n') + '\n', 'text/csv');
          }}>Download statistics (CSV)</button>
        </>
      )}
      <button class="btn wide" data-testid="export-profile" onClick={() => {
        const c = profile.domain.candidates[profile.domain.selected];
        download(`${baseName(profile)}.profile.json`, JSON.stringify({
          cogProbeVersion: profile.cogProbeVersion, url: profile.url,
          probedAt: profile.probedAt, raster: profile.raster, sampling: profile.sampling,
          domain: c && { id: c.id, label: c.label, perBand: !!c.perBand,
            scale: c.perBand ? null : c.scale, offset: c.perBand ? null : c.offset,
            scales: c.scales ?? null, offsets: c.offsets ?? null,
            confidence: c.confidence, ambiguous: profile.domain.ambiguous },
          render: { mode: state.mode, index: state.mode === 'index' ? state.indexId : null,
            ramp: state.rampId, min: state.lo, max: state.hi },
          bands: profile.bands.map((b) => ({
            index: b.index, glslIndex: b.glslIndex, role: b.role,
            wavelengthNm: b.wavelengthNm, source: b.source, why: b.why,
            description: b.description, isAlpha: b.isAlpha,
            scale: b.scale, offset: b.offset,
            stats: { min: b.stats.min, p2: b.stats.p2, p50: b.stats.p50,
              p98: b.stats.p98, max: b.stats.max, mean: b.stats.mean,
              validFraction: b.stats.validFraction },
          })),
          warnings: [...profile.warnings, ...profile.bandNotes]
            .map((w) => ({ level: w.level, text: w.text.replace(/\*\*/g, '') })),
        }, null, 2));
      }}>Download profile (JSON)</button>
      {state.classes.some((c) => c.points.length) && (
        <button class="btn wide" data-testid="export-spectra" onClick={() =>
          download(`${baseName(profile)}.spectra.csv`, spectraCSV(state.classes)!, 'text/csv')}>
          Download class spectra (CSV)
        </button>
      )}
    </section>
  );
}
